import React from 'react'
import classNames from 'classnames'
import {NullAsDefault, renderIf} from 'views/Shared'
import {CurriculumActionTypes} from 'views/AppLocals/Course'
import {Image} from 'antd'
import {BsChevronRight, BsClock, BsEyeFill} from 'react-icons/bs'
import {RiSubscript} from 'react-icons/ri'
import {PiMedalFill} from 'react-icons/pi'
import {getGroupColor} from './renderCourseClassStudent'
import {ImagePlaceholder} from '../../../../conts/imageBase64'

// Avatar chương trình học = chữ cái đầu của 2 từ đầu tên (vd "Giáo lý Hôn nhân" -> "GL"), tô màu
// theo tên qua getGroupColor dùng chung - cùng quy tắc avatar chữ ở Học viên / Kho Môn học.
const getCurriculumInitials = (title) => {
    if (!title) return '?'
    const words = title.trim().split(/\s+/).filter(Boolean)
    return (words.slice(0, 2).map((w) => w.charAt(0)).join('') || '?').toUpperCase()
}

const CurriculumAvatar = ({title, size = 32}) => (
    <div
        style={{width: size, height: size, backgroundColor: getGroupColor(title), flexShrink: 0}}
        className="rounded-full flex flex-center">
        <span className={classNames('text-white font-bold leading-none', size >= 40 ? 'text-sm' : 'text-xs')}>
            {getCurriculumInitials(title)}
        </span>
    </div>
)

const DurationPill = ({months, t}) => renderIf(
    months > 0,
    <span className="w-fit flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-2xs font-bold text-blue-600 whitespace-no-wrap">
        <BsClock size={11}/>
        {`${months} ${t('months')}`}
    </span>
)

// Số môn - GHI NHẬN đủ (luôn hiện, kể cả 0) thay cho cột "Môn học thành phần" liệt kê từng môn ở
// bảng (đã bỏ, xem renderCurriculumTableRow) - danh sách môn đầy đủ + thêm/bớt môn chỉ còn nằm
// trong trang chi tiết (bấm vào chương trình học, đã có sẵn phần thêm môn ở đó, không lặp lại ở
// bảng). 0 môn tô nhạt để phân biệt "chưa gắn môn nào" với "đã có N môn".
const SubjectCountPill = ({count = 0, t}) => (
    <span className={classNames(
        'w-fit flex items-center gap-1 px-2 py-0.5 border rounded-full text-2xs font-bold whitespace-no-wrap',
        count > 0 ? 'bg-slate-50 border-slate-200 text-slate-600' : 'bg-color-50 border-color-100 text-color-400'
    )}>
        <RiSubscript size={11}/>
        {`${count} ${t('subjects')}`}
    </span>
)

const SubjectChips = ({subjects = [], t}) => {
    if (!subjects.length) return null
    return (
        <div className="flex flex-wrap gap-1.5">
            {subjects.map((s, i) => (
                <span
                    key={s.id || i}
                    className="px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-full text-2xs font-medium text-slate-500 tracking-wide whitespace-no-wrap">
                    {s.title}
                    {s.number_of_sessions > 0 && (
                        <span className="ml-1 text-slate-400 italic">
                            {`(${s.number_of_sessions}${t('sessions')[0]})`}
                        </span>
                    )}
                </span>
            ))}
        </div>
    )
}

export const renderCurriculum = ({
    translate: t,
    renderContextMenu = NullAsDefault,
    handleAction = NullAsDefault,
} = {}) => (item, index) => {

    const {title, description, duration_months, subjects = []} = item ?? {}

    return (
        <div key={index} className="p-4 flex flex-col custom-item-render relative list-item">
            <div className="flex flex-wrap items-center gap-2 leading-none">
                <h2
                    onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_READ, item)}
                    className="font-bold text-base text-gray-800 tracking-wide hover:text-primary cursor-pointer leading-none">
                    {title}
                </h2>
                {renderIf(
                    duration_months > 0,
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm font-medium text-blue-600 tracking-wide leading-none">
                        <BsClock size={12}/>
                        {`${t('duration')}: ${duration_months} ${t('months')}`}
                    </span>
                )}
            </div>
            <div className="my-2 border-b border-color-100" />
            {renderIf(
                subjects.length > 0,
                <div className="flex flex-wrap gap-2">
                    {subjects.map((s, i) => (
                        <span
                            key={s.id || i}
                            className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-full text-sm font-medium text-slate-500 tracking-wide leading-tight">
                            {s.title}
                            {s.number_of_sessions > 0 && (
                                <span className="ml-1 font-medium text-slate-400 italic">
                                    {`(${s.number_of_sessions}${t('sessions')[0]})`}
                                </span>
                            )}
                        </span>
                    ))}
                </div>
            )}

            {renderIf(
                description,
                <div className="mt-2 text-color-300 tracking-wider text-sm max-lines-2">
                    {description}
                </div>
            )}

            <div className="absolute right-10 top-10 contextIcon">
                {renderContextMenu(item)}
            </div>
        </div>
    )
}

// Layout "Bảng" (mặc định) + "Thẻ" cho trang Chương trình học (Curricula.js). KHÔNG đổi
// renderCurriculum gốc phía trên. Cột: CHƯƠNG TRÌNH HỌC | THỜI LƯỢNG | MÔN HỌC THÀNH PHẦN |
// ẢNH CHỨNG NHẬN ĐẠT ĐƯỢC | THAO TÁC.
// Box "Chứng nhận đạt được" dùng chung grid + list. `compact` = biến thể cho ô bảng (ảnh nhỏ
// hơn, có link "Xem phôi bảng"). Bấm vào cả block để xem ảnh phóng to (antd preview controlled).
export const AchievedCertBox = ({medal, t, compact = false}) => {
    const [preview, setPreview] = React.useState(false)
    const level = medal?.lvl_number
    const hasImage = !!medal?.avatar
    const imgSize = compact ? 44 : 56
    return (
        <div
            onClick={() => hasImage && setPreview(true)}
            style={compact ? {maxWidth: 300} : undefined}
            className={classNames(
                "rounded-xl border border-amber-200 border-l-4 border-l-amber-400 bg-amber-50 flex items-center",
                compact ? "px-2.5 py-2 gap-2.5" : "px-3 py-3 gap-3",
                hasImage && "cursor-zoom-in"
            )}>
            {hasImage ? (
                <>
                    <img
                        src={medal.avatar}
                        alt=""
                        style={{width: imgSize, height: imgSize, objectFit: 'contain'}}
                        className="flex-shrink-0 rounded-lg background"
                    />
                    <Image
                        src={medal.avatar}
                        fallback={ImagePlaceholder}
                        style={{display: 'none'}}
                        preview={{visible: preview, onVisibleChange: (v) => setPreview(v)}}
                    />
                </>
            ) : (
                <PiMedalFill size={compact ? 22 : 28} className="text-amber-500 flex-shrink-0"/>
            )}
            <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                {!compact && (
                    <span className="text-2xs font-bold text-amber-600 uppercase tracking-wide">
                        {t('achieved certificate')}
                    </span>
                )}
                {medal?.title
                    ? <span className={classNames("font-semibold text-color-000 truncate", compact ? "text-xs" : "text-sm")}>{medal.title}</span>
                    : <span className="text-xs text-color-400 italic">{t('no certificate assigned')}</span>}
                {compact && hasImage && (
                    <span className="w-fit text-2xs font-medium text-amber-600 hover:text-amber-700 underline cursor-pointer">
                        {`${t('view certificate template')} →`}
                    </span>
                )}
            </div>
            {(level !== undefined && level !== null && level !== '') && (
                <span className="flex-shrink-0 self-start px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-2xs font-semibold text-amber-600 whitespace-no-wrap">
                    {`${t('level')} ${level}`}
                </span>
            )}
        </div>
    )
}

export const renderCurriculumTableRow = ({
    translate: t,
    renderContextMenu = NullAsDefault,
    handleAction = NullAsDefault,
} = {}) => (item, index) => {
    const {title, duration_months, subjects = [], default_medal} = item ?? {}
    const openCourses = item?.course_count ?? item?.courses_count ?? item?.open_course_count ?? 0
    return (
        <tr key={index} className="border-t border-color-50 background hover:background-50 transition-colors align-top">
            {/* CHƯƠNG TRÌNH HỌC (kèm thời lượng + số môn ngay dưới tên) */}
            <td className="px-3 py-2.5">
                <div className="flex items-start gap-2.5 min-w-0" style={{maxWidth: 240}}>
                    <CurriculumAvatar title={title}/>
                    <div className="flex flex-col gap-1 min-w-0">
                        <span
                            onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_READ, item)}
                            className="font-bold text-sm text-color-000 hover:text-primary cursor-pointer leading-snug">
                            {title}
                        </span>
                        <div className="flex items-center flex-wrap gap-1.5">
                            {duration_months > 0 && <DurationPill months={duration_months} t={t}/>}
                            <SubjectCountPill count={subjects.length} t={t}/>
                        </div>
                    </div>
                </div>
            </td>
            {/* SỐ KHÓA (chỉ hiển thị value) */}
            <td className="px-3 py-2.5">
                <span className={classNames('text-sm font-bold', openCourses > 0 ? 'text-gray-700' : 'text-color-300')}>
                    {openCourses}
                </span>
            </td>
            {/* ẢNH CHỨNG NHẬN ĐẠT ĐƯỢC */}
            <td className="px-3 py-2.5">
                <AchievedCertBox medal={default_medal} t={t} compact/>
            </td>
            {/* THAO TÁC */}
            <td className="px-3 py-2.5">
                <div className="flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_READ, item)}
                        style={{width: 28, height: 28}}
                        className="flex flex-center rounded-full border border-color-200 hover:border-primary hover:background-100 text-color-400 hover:text-primary">
                        <BsEyeFill size={13}/>
                    </button>
                    {renderContextMenu(item)}
                </div>
            </td>
        </tr>
    )
}

export const renderCurriculumCardItem = ({
    translate: t,
    renderContextMenu = NullAsDefault,
    handleAction = NullAsDefault,
} = {}) => (item, index) => {
    const {title, duration_months, subjects = [], default_medal} = item ?? {}
    // "Số khóa đang mở" - tên field BE chưa chắc, thử vài khả năng rồi fallback 0.
    const openCourses = item?.course_count ?? item?.courses_count ?? item?.open_course_count ?? 0

    return (
        <div
            key={index}
            className="p-4 flex flex-col gap-3 border border-color-100 background rounded-2xl hover:shadow-md transition-all duration-300 custom-item-render">
            {/* Header: avatar + title | pill thời lượng */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5 min-w-0">
                    <CurriculumAvatar title={title} size={40}/>
                    <span
                        onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_READ, item)}
                        className="font-bold text-base text-color-000 hover:text-primary cursor-pointer leading-snug max-lines-2">
                        {title}
                    </span>
                </div>
                <DurationPill months={duration_months} t={t}/>
            </div>

            {/* Chứng nhận đạt được */}
            <AchievedCertBox medal={default_medal} t={t}/>

            {/* Môn học thành phần */}
            <div className="rounded-xl background-100 px-3 py-2.5 flex flex-col gap-1.5">
                <span className="text-2xs font-bold text-color-400 uppercase tracking-wide">
                    {`${t('component subjects')}:`}
                </span>
                {subjects.length > 0
                    ? <SubjectChips subjects={subjects} t={t}/>
                    : <span className="text-xs text-color-400 italic">{t('no subjects linked')}</span>}
            </div>

            {/* Footer */}
            <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-color-50">
                <span className="text-2xs text-color-400 whitespace-no-wrap">
                    {`${t('open courses count')}: `}
                    <span className="font-bold text-gray-600">{openCourses}</span>
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <div className="contextIcon">
                        {renderContextMenu(item)}
                    </div>
                    <button
                        type="button"
                        onClick={() => handleAction(CurriculumActionTypes.CURRICULUM_READ, item)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-600 font-semibold text-2xs whitespace-no-wrap">
                        {t('view detail')}
                        <BsChevronRight size={11}/>
                    </button>
                </div>
            </div>
        </div>
    )
}
